import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    useMaterialReactTable,
    MaterialReactTable,
} from 'material-react-table';
import { Box, Typography, Link, Chip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import apiService from '../../services/apiService';
import { useTableUrlState } from '../../hooks/useTableUrlState';
import {ROLES} from "../../config/roles";

const PlayersPage = () => {
    // Data state from the paginated API
    const [data, setData] = useState([]);
    const [rowCount, setRowCount] = useState(0);

    // API fetching state
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    // Table state for pagination and sorting
    const { pagination, setPagination, sorting, setSorting } = useTableUrlState({
        initialSorting: [{ id: 'createdAt', desc: true }],
    });

    // Server-side data fetching logic
    const fetchData = useCallback(() => {
        setIsLoading(true);
        const params = new URLSearchParams({
            page: pagination.pageIndex,
            size: pagination.pageSize,
        });

        if (sorting.length > 0) {
            params.append('sort', `${sorting[0].id},${sorting[0].desc ? 'desc' : 'asc'}`);
        }

        apiService.get(`/api/players?${params.toString()}`)
            .then(page => {
                setData(page.content);
                setRowCount(page.totalElements);
                setIsError(false);
            })
            .catch(() => setIsError(true))
            .finally(() => setIsLoading(false));
    }, [pagination, sorting]);

    // Trigger the fetch when state changes
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Define table columns
    const columns = useMemo(
        () => [
            {
                accessorKey: 'id',
                id: 'uid',
                header: 'Player ID',
                size: 250,
                Cell: ({ cell, row }) => (
                    <Link component={RouterLink} to={`/players/${cell.getValue()}`}>
                        {cell.getValue()}
                    </Link>
                ),
            },
            {
                accessorKey: 'name',
                header: 'Name',
            },
            {
                accessorKey: 'nickName',
                header: 'Nickname',
                Cell: ({ cell }) => cell.getValue() || '—',
            },
            {
                accessorKey: 'email',
                header: 'Email',
            },
            {
                accessorKey: 'active',
                id: 'isActive',
                header: 'Active',
                size: 100,
                Cell: ({ cell }) => (
                    <Chip
                        label={cell.getValue() ? 'Yes' : 'No'}
                        color={cell.getValue() ? 'success' : 'default'}
                        size="small"
                    />
                ),
            },
            {
                accessorKey: 'online',
                id: 'online',
                header: 'Online',
                size: 110,
                // Live presence comes from Redis, not the DB — not server-sortable/filterable.
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ cell }) => (
                    <Chip
                        label={cell.getValue() ? 'Online' : 'Offline'}
                        color={cell.getValue() ? 'success' : 'default'}
                        variant={cell.getValue() ? 'filled' : 'outlined'}
                        size="small"
                    />
                ),
            },
            {
                // "Last Seen" = last_seen_at, stamped on every /auth login. The old logout_at
                // binding had no writer, and updated_at only moves on profile edits — both were
                // wrong for activity.
                accessorKey: 'lastSeenAt',
                header: 'Last Seen',
                Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleString() : '—',
            },
            {
                accessorKey: 'createdAt',
                header: 'Joined',
                Cell: ({ cell }) => new Date(cell.getValue()).toLocaleString(),
            },
        ],
        []
    );

    const table = useMaterialReactTable({
        columns,
        data,
        // Server-side configuration
        manualPagination: true,
        manualSorting: true,
        rowCount,
        // State Management
        state: {
            pagination,
            sorting,
            isLoading,
            showAlertBanner: isError,
        },
        // State Handlers
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        // UI configuration
        muiToolbarAlertBannerProps: isError
            ? { color: 'error', children: 'Error loading data' }
            : undefined,
        initialState: {
            showGlobalFilter: true, // Allow client-side search on the current page
        },
    });

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h4" gutterBottom>
                Players
            </Typography>
            <MaterialReactTable table={table} />
        </Box>
    );
};

PlayersPage.abilities = {
    read: [ROLES.SUPPORT, ROLES.ADMINS, ROLES.VIEWERS],
    create: [ROLES.ADMINS],
    delete: [ROLES.ADMINS],
};

export default PlayersPage;